import { useState } from 'react';

import { PlaceAutocomplete, type PlaceResult } from './place-autocomplete';

/** One of the customer's places on an area map: marked with a heart, with their text */
export interface PlaceLabel {
  text: string;
  lat: number;
  lon: number;
}

export const MAX_PLACE_LABELS = 5;
export const MAX_LABEL_LENGTH = 20;

interface PlaceLabelsProps {
  value: PlaceLabel[];
  onChange: (labels: PlaceLabel[]) => void;
  disabled?: boolean;
}

function Heart({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#D7263D" aria-hidden="true">
      <path d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 3 4.5 6.7 4.5c2.1 0 3.6 1.2 5.3 3.1 1.7-1.9 3.2-3.1 5.3-3.1 3.7 0 5.8 3.9 4.3 7.3C19.5 16.4 12 21 12 21z" />
    </svg>
  );
}

/** "Label your places": up to 5 of the customer's own places, each with editable text */
export function PlaceLabels({ value, onChange, disabled }: PlaceLabelsProps) {
  const [adding, setAdding] = useState(false);

  const add = (place: PlaceResult) => {
    const name = (place.areaName || place.city || place.formattedAddress.split(',')[0]).trim();
    onChange([...value, { text: name.slice(0, MAX_LABEL_LENGTH), lat: place.lat, lon: place.lon }]);
    setAdding(false);
  };

  // Text is saved when the field loses focus, so the preview redraws once, not on every keystroke
  const rename = (index: number, text: string) => {
    const trimmed = text.trim();
    if (!trimmed || trimmed === value[index].text) return;
    onChange(value.map((label, i) => (i === index ? { ...label, text: trimmed } : label)));
  };

  return (
    <div className="border-neutral-2-20 space-y-3 border-t pt-5">
      <div>
        <span className="text-primary-2-80 block text-sm font-medium">Label Your Places</span>
        <span className="text-neutral-2-60 block text-xs">
          Add your city, home town or anywhere special. We'll mark it with a heart
        </span>
      </div>

      {value.length > 0 && (
        <ul className="space-y-2">
          {value.map((label, index) => (
            <li key={`${label.lat},${label.lon}`} className="flex items-center gap-2">
              <Heart className="h-5 w-5 shrink-0" />
              <input
                type="text"
                defaultValue={label.text}
                maxLength={MAX_LABEL_LENGTH}
                onBlur={(e) => rename(index, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                disabled={disabled}
                aria-label="Label text"
                className="border-neutral-2-20 focus:border-secondary-1 focus:ring-secondary-1 block w-full rounded-md border px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-neutral-2-10"
              />
              <button
                type="button"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                disabled={disabled}
                aria-label={`Remove ${label.text}`}
                className="text-neutral-2-60 hover:text-primary-2-80 shrink-0 p-1 text-lg leading-none disabled:cursor-not-allowed"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className="space-y-2">
          <PlaceAutocomplete
            onPlaceSelect={add}
            disabled={disabled}
            showExtras={false}
            label="Add a place"
            placeholder="Search for a city or town..."
            helperText="You can change the name shown, e.g. to 'Home' or 'Where we met'"
          />
          <button
            type="button"
            onClick={() => setAdding(false)}
            className="text-neutral-2-60 hover:text-primary-2-80 text-xs font-medium"
          >
            Cancel
          </button>
        </div>
      ) : (
        value.length < MAX_PLACE_LABELS && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={disabled}
            className="border-neutral-2-20 text-secondary-1 hover:border-secondary-1 flex w-full items-center justify-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Heart className="h-4 w-4" />
            Add a place {value.length > 0 && `(${value.length}/${MAX_PLACE_LABELS})`}
          </button>
        )
      )}
    </div>
  );
}
